"use client";

import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const NAMESPACE = "new-aluminum-extrusion";
const IMAGE_BASE = "/image/new-version/more/AluminumExtrusion/applications-of-aluminum-extrusion";

const APPLICATIONS = [
	{ keyPrefix: "applicationsItem1", image: "industrial.webp", href: "/industry/industry-equipment" },
	{ keyPrefix: "applicationsItem4", image: "energy.webp", href: "/industry/renewable-energy" },
	{ keyPrefix: "applicationsItem2", image: "Transportation.webp", href: null },
	{ keyPrefix: "applicationsItem5", image: "electronics.webp", href: "/industry/consumer-electronics" },
	{ keyPrefix: "applicationsItem3", image: "construction.webp", href: null },
	{ keyPrefix: "applicationsItem6", image: "medical.webp", href: "/industry/medical-device" },
] as const;

export default function AluminumExtrusionApplications() {
	const t = useTranslations(NAMESPACE);
	const [activeKey, setActiveKey] = useState<string | null>("applicationsItem6");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
						{t("applicationsEyebrow")}
					</span>
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("applicationsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">
						{t("applicationsDescBefore")}
						<strong className="font-bold">{t("applicationsDescBold")}</strong>
						{t("applicationsDescAfter")}
					</p>
				</div>

				<div className="grid items-start gap-8 lg:grid-cols-[6fr_4fr] lg:gap-10 xl:gap-14">
					<div className="grid gap-x-10 gap-y-6 sm:grid-cols-2">
						{APPLICATIONS.map((item) => (
							<div key={item.keyPrefix}>
								<h3 className="mb-2 text-base font-semibold text-themePrimary400 sm:text-lg">
									{t(`${item.keyPrefix}Title`)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">
									{t(`${item.keyPrefix}Desc`)}
								</p>
							</div>
						))}
					</div>

					<div className="grid grid-cols-2 gap-3 self-start">
						{APPLICATIONS.map((item) => (
							item.href ? (
								<Link
									key={`${item.keyPrefix}Image`}
									href={item.href}
									onClick={() => setActiveKey(item.keyPrefix)}
									className={`group flex min-h-32 flex-col rounded-xl p-4 transition-colors duration-300 ${
										activeKey === item.keyPrefix ? "bg-[#88d43c]" : "bg-[#ededed] hover:bg-[#88d43c]"
									}`}
								>
									<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
										<Image
											src={`${IMAGE_BASE}/${item.image}`}
											alt={t(`${item.keyPrefix}ImageAlt`)}
											fill
											className="object-cover"
											sizes="80px"
										/>
									</div>
									<span
										className={`mt-auto self-end text-right text-sm font-semibold sm:text-base ${
											activeKey === item.keyPrefix ? "text-white" : "text-[#1f1f1f] group-hover:text-white"
										}`}
									>
										{t(`${item.keyPrefix}Title`)}
									</span>
								</Link>
							) : (
								<button
									key={`${item.keyPrefix}Image`}
									type="button"
									onClick={() => setActiveKey(item.keyPrefix)}
									aria-pressed={activeKey === item.keyPrefix}
									className={`group flex min-h-32 flex-col rounded-xl p-4 text-left transition-colors duration-300 ${
										activeKey === item.keyPrefix ? "bg-[#88d43c]" : "bg-[#ededed] hover:bg-[#88d43c]"
									}`}
								>
									<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
										<Image
											src={`${IMAGE_BASE}/${item.image}`}
											alt={t(`${item.keyPrefix}ImageAlt`)}
											fill
											className="object-cover"
											sizes="80px"
										/>
									</div>
									<span
										className={`mt-auto self-end text-right text-sm font-semibold sm:text-base ${
											activeKey === item.keyPrefix ? "text-white" : "text-[#1f1f1f] group-hover:text-white"
										}`}
									>
										{t(`${item.keyPrefix}Title`)}
									</span>
								</button>
							)
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
